import { 
  collection as firestoreCollection, 
  doc as firestoreDoc, 
  addDoc as firestoreAddDoc,
  setDoc as firestoreSetDoc,
  updateDoc as firestoreUpdateDoc,
  deleteDoc as firestoreDeleteDoc,
  getDoc as firestoreGetDoc,
  getDocs as firestoreGetDocs,
  query as firestoreQuery,
  onSnapshot as firestoreOnSnapshot,
  writeBatch as firestoreWriteBatch,
  CollectionReference,
  DocumentReference,
  DocumentData,
  QueryConstraint,
  Query,
  Firestore,
  serverTimestamp,
  WithFieldValue,
  UpdateData,
  Unsubscribe,
  QuerySnapshot,
  DocumentSnapshot,
  WriteBatch,
  PartialWithFieldValue,
  SetOptions,
  where,
} from 'firebase/firestore';
import logger from '@/lib/logging';
import { getDb } from '@/lib/firebase-config';
import { User } from 'firebase/auth';
import Cookies from 'js-cookie';

/**
 * Cookie name for guest user ID
 */
const GUEST_ID_COOKIE = 'harry_guest_id';
const GUEST_ID_COOKIE_EXPIRY_DAYS = 365; // Guest sessions last 1 year

/**
 * User context interface
 */
export interface UserContext {
  userId: string;
  isGuest: boolean;
  user?: User | null;
}

/**
 * Generate a unique guest ID
 */
export function generateGuestId(): string {
  // Generate a unique ID with timestamp and random component
  const timestamp = Date.now().toString(36);
  const randomPart = Math.random().toString(36).substring(2, 12);
  return `${timestamp}_${randomPart}`;
}

/**
 * Get or create guest ID from HttpOnly cookie via server endpoint
 * This function calls the server to set/get the HttpOnly guest cookie
 */
export async function getOrCreateGuestId(): Promise<string> {
  // First, try to get existing guest cookie from server
  try {
    const response = await fetch('/api/auth/guest-cookie', {
      method: 'GET',
      credentials: 'include', // Include cookies in request
    });
    
    if (response.ok) {
      const data = await response.json();
      if (data.exists && data.guestId) {
        return data.guestId;
      }
    }
  } catch (error) {
    logger.warn('⚠️ [GUEST] Failed to check existing guest cookie:', error);
  }
  
  // No existing cookie, create new one via server
  try {
    const response = await fetch('/api/auth/guest-cookie', {
      method: 'POST',
      credentials: 'include', // Include cookies in request
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    if (response.ok) {
      const data = await response.json();
      if (data.guestId) {
        return data.guestId;
      }
    }
    
    throw new Error('Failed to create guest cookie');
  } catch (error) {
    logger.error('❌ [GUEST] Failed to create guest cookie via server:', error);
    // Fallback: Generate client-side ID (not ideal, but allows app to continue)
    // This should only happen if server is unavailable
    const fallbackId = generateGuestId();
    logger.warn('⚠️ [GUEST] Using fallback client-side guest ID:', fallbackId);
    return fallbackId;
  }
}

/**
 * Synchronous fallback: Get guest ID from client-side cookie (for backward compatibility)
 * Note: This will not work if cookie is HttpOnly, but provides fallback during migration
 */
function getGuestIdFromCookie(): string | null {
  return Cookies.get(GUEST_ID_COOKIE) || null;
}

/**
 * Clear guest ID cookie via server endpoint (used when user logs in)
 */
export async function clearGuestId(): Promise<void> {
  try {
    await fetch('/api/auth/guest-cookie', {
      method: 'DELETE',
      credentials: 'include', // Include cookies in request
    });
    logger.debug('✅ [GUEST] Guest cookie cleared via server');
  } catch (error) {
    logger.error('❌ [GUEST] Failed to clear guest cookie via server:', error);
    // Fallback: Try to clear client-side cookie (may not exist if HttpOnly)
    Cookies.remove(GUEST_ID_COOKIE);
  }
}

/**
 * Get the current user context (synchronous version)
 * For authenticated users, returns immediately.
 * For guests, tries to read from cookie first, then falls back to generating ID.
 * Note: For proper HttpOnly cookie support, use initializeGuestContext() instead.
 */
export function getUserContext(user: User | null): UserContext {
  if (user) {
    // Authenticated user
    const context = {
      userId: user.uid,
      isGuest: false,
      user
    };
    return context;
  } else {
    // Guest user - try to get from cookie first (fallback during migration)
    let guestId = getGuestIdFromCookie();
    
    if (!guestId) {
      // Generate temporary ID - will be replaced by server cookie on next request
      guestId = generateGuestId();
    }
    
    const context = {
      userId: `guest_${guestId}`,
      isGuest: true,
      user: null
    };
    return context;
  }
}

/**
 * Initialize guest context with HttpOnly cookie (async)
 * Call this early in the app lifecycle to ensure guest cookie is set via server
 */
export async function initializeGuestContext(): Promise<UserContext | null> {
  // Only initialize if no user is authenticated
  if (typeof window === 'undefined') {
    return null; // SSR
  }
  
  try {
    const guestId = await getOrCreateGuestId();
    return {
      userId: `guest_${guestId}`,
      isGuest: true,
      user: null
    };
  } catch (error) {
    logger.error('❌ [GUEST] Failed to initialize guest context:', error);
    return null;
  }
}

/**
 * Attribute-Based Database Wrapper Class
 * Automatically adds allowRead/allowWrite attributes to documents
 * and filters queries based on user permissions
 */
export class ScopedDatabase {
  private db: Firestore | null;
  private userContext: UserContext;
  
  constructor(userContext: UserContext) {
    // Safely get DB - return null during SSR
    try {
      this.db = typeof window !== 'undefined' ? getDb() : null;
    } catch (error) {
      logger.warn('Failed to get Firestore database (likely SSR):', error);
      this.db = null;
    }
    this.userContext = userContext;
  }
  
  private ensureDb(): Firestore {
    if (!this.db) {
      throw new Error('Firestore database is not available (likely during SSR)');
    }
    return this.db;
  }
  
  /**
   * Inject access control attributes into document data
   */
  private injectAccessControl<T extends DocumentData>(data: T): T {
    return {
      ...data,
      allowRead: [this.userContext.userId],
      allowWrite: [this.userContext.userId],
      _lastModified: serverTimestamp(),
      _createdBy: this.userContext.userId,
      _isGuest: this.userContext.isGuest,
    };
  }
  
  /**
   * Inject only update metadata (not changing ownership)
   */
  private injectUpdateMetadata<T extends DocumentData>(data: T): T {
    return {
      ...data,
      _lastModified: serverTimestamp(),
    };
  }
  
  /**
   * Get a collection reference (no path scoping)
   */
  collection(path: string): CollectionReference<DocumentData> {
    const db = this.ensureDb();
    logger.debug('📁 ScopedDatabase.collection() called:');
    logger.debug('  Collection path:', path);
    logger.debug('  User ID:', this.userContext.userId);
    logger.debug('  Is Guest:', this.userContext.isGuest);
    return firestoreCollection(db, path);
  }
  
  /**
   * Get a document reference (no path scoping)
   */
  doc(path: string, ...pathSegments: string[]): DocumentReference<DocumentData> {
    const db = this.ensureDb();
    logger.debug('📄 ScopedDatabase.doc() called:');
    logger.debug('  Document path:', [path, ...pathSegments].join('/'));
    logger.debug('  User ID:', this.userContext.userId);
    return firestoreDoc(db, path, ...pathSegments);
  }
  
  /**
   * Add a document with access control attributes
   */
  async addDoc<T extends DocumentData>(
    collectionRef: CollectionReference<T>,
    data: WithFieldValue<T>
  ): Promise<DocumentReference<T>> {
    const dataWithAccess = this.injectAccessControl(data);
    logger.debug('➕ Adding document with access control:', {
      collectionPath: collectionRef.path,
      allowRead: dataWithAccess.allowRead,
      allowWrite: dataWithAccess.allowWrite,
    });
    return firestoreAddDoc(collectionRef, dataWithAccess);
  }
  
  /**
   * Set a document with access control attributes
   */
  async setDoc<T extends DocumentData>(
    docRef: DocumentReference<T>,
    data: WithFieldValue<T>,
    options?: SetOptions
  ): Promise<void> {
    const isMergeWrite = Boolean(options && 'merge' in options && (options as any).merge);
    const dataWithAccess = isMergeWrite
      ? this.injectUpdateMetadata(data) 
      : this.injectAccessControl(data);
    logger.debug('📝 Setting document with access control:', {
      documentPath: docRef.path,
      merge: isMergeWrite,
      allowRead: (dataWithAccess as any).allowRead,
      allowWrite: (dataWithAccess as any).allowWrite,
    });
    return firestoreSetDoc(docRef, dataWithAccess, options || {});
  }
  
  /**
   * Update a document with metadata (not changing ownership)
   */
  async updateDoc<T extends DocumentData>(
    docRef: DocumentReference<T>,
    data: UpdateData<T>
  ): Promise<void> {
    const dataWithMetadata = this.injectUpdateMetadata(data as any);
    logger.debug('✏️ Updating document:', {
      documentPath: docRef.path,
    });
    return firestoreUpdateDoc(docRef, dataWithMetadata);
  }
  
  /**
   * Delete a document
   */
  async deleteDoc(docRef: DocumentReference): Promise<void> {
    logger.debug('🗑️ Deleting document:', docRef.path);
    return firestoreDeleteDoc(docRef);
  }
  
  /**
   * Get a document (will be filtered by Firestore rules)
   */
  async getDoc(docRef: DocumentReference): Promise<DocumentSnapshot> {
    return firestoreGetDoc(docRef);
  }
  
  /**
   * Get documents from a query
   */
  async getDocs(query: Query): Promise<QuerySnapshot> {
    return firestoreGetDocs(query);
  }
  
  /**
   * Create a query with automatic user filtering
   * For collections with access control, automatically adds allowRead filter
   * For child collections, inherits access from parent documents
   */
  query(
    collection: CollectionReference,
    ...constraints: QueryConstraint[]
  ): Query {
    const collectionPath = collection.path;
    const pathSegments = collectionPath.split('/');
    const collectionName = pathSegments[pathSegments.length - 1]; // Get the last segment (actual collection name)
    const isNestedUnderClient = pathSegments.length >= 3 && pathSegments[0] === 'clients';
    
    // Collections that have their own access control (user-scoped data)
    // Note: 'drills' and 'categories' are SHARED resources, not user-scoped
    const directAccessCollections = ['clients', 'subscriptions', 'programs'];
    
    // Collections that inherit access from parent client (when nested)
    const inheritsFromClient = ['assessments', 'postureAnalyses'];
    
    logger.debug('🔍 ScopedDatabase Query Details:');
    logger.debug('   Collection path:', collectionPath);
    logger.debug('   Collection name:', collectionName);
    logger.debug('   Is nested under client:', isNestedUnderClient);
    logger.debug('   User ID:', this.userContext.userId);
    logger.debug('   Is Guest:', this.userContext.isGuest);
    
    // For root-level clients, drills, categories - apply direct access filter
    // This applies to BOTH authenticated users AND guest users to ensure proper data isolation
    if (directAccessCollections.includes(collectionName) && !isNestedUnderClient) {
      logger.debug('   ➡️ Adding allowRead filter: allowRead array-contains ' + this.userContext.userId);
      return firestoreQuery(
        collection, 
        where('allowRead', 'array-contains', this.userContext.userId),
        ...constraints
      );
    }
    
    // For nested assessments/postureAnalyses under clients - no filter needed (handled by Firestore rules)
    if (isNestedUnderClient && inheritsFromClient.includes(collectionName)) {
      logger.debug('   ➡️ Nested collection inherits access from parent client - no filter needed');
      return firestoreQuery(collection, ...constraints);
    }
    
    // For other collections, return query as-is
    logger.debug('   ➡️ No special filtering applied (shared collection)');
    return firestoreQuery(collection, ...constraints);
  }
  
  /**
   * Listen to a query or document
   */
  onSnapshot(
    target: Query | DocumentReference,
    onNext: (snapshot: QuerySnapshot | DocumentSnapshot) => void,
    onError?: (error: Error) => void
  ): Unsubscribe {
    logger.debug('👂 Setting up snapshot listener');
    if ('type' in target && target.type === 'document') {
      return firestoreOnSnapshot(target as DocumentReference, onNext as any, onError);
    }
    return firestoreOnSnapshot(target as Query, onNext as any, onError);
  }
  
  /**
   * Create a write batch
   */
  batch(): WriteBatch {
    const batch = firestoreWriteBatch(this.ensureDb());
    
    // Wrap batch methods to inject access control
    const originalSet = batch.set.bind(batch);
    const originalUpdate = batch.update.bind(batch);
    
    batch.set = <T extends DocumentData>(
      docRef: DocumentReference<T>,
      data: PartialWithFieldValue<T>,
      options?: SetOptions
    ) => {
      const isMergeWrite = Boolean(options && 'merge' in options && (options as any).merge);
      const dataWithAccess = isMergeWrite
        ? this.injectUpdateMetadata(data as T) 
        : this.injectAccessControl(data as T);
      return originalSet(docRef, dataWithAccess, options || {});
    };
    
    batch.update = <T extends DocumentData>(
      docRef: DocumentReference<T>,
      data: UpdateData<T>
    ) => {
      const dataWithMetadata = this.injectUpdateMetadata(data as any);
      return originalUpdate(docRef, dataWithMetadata);
    };
    
    return batch;
  }
  
  /**
   * Get the current user context
   */
  getUserContext(): UserContext {
    return this.userContext;
  }
  
  /**
   * Check if current user is a guest
   */
  isGuest(): boolean {
    return this.userContext.isGuest;
  }
  
/**
 * Get accessible client IDs for the current user
 * Used to filter child collections that inherit permissions
 */
async getAccessibleClientIds(): Promise<string[]> {
  if (this.userContext.isGuest) {
    // Guests only see their own data
    const clientsCollection = this.collection('clients');
    const clientsQuery = firestoreQuery(
      clientsCollection,
      where('_createdBy', '==', this.userContext.userId)
    );
    const snapshot = await this.getDocs(clientsQuery);
    return snapshot.docs.map(doc => doc.id);
  }
  
  // Authenticated users see clients where they have allowRead permission
  const clientsCollection = this.collection('clients');
  const clientsQuery = firestoreQuery(
    clientsCollection,
    where('allowRead', '==', this.userContext.userId)
  );
  const snapshot = await this.getDocs(clientsQuery);
  return snapshot.docs.map(doc => doc.id);
}

/**
 * Helper to share a document with another user
 */
async shareDocument(
  docRef: DocumentReference,
  targetUserId: string,
  permission: 'read' | 'write' = 'read'
): Promise<void> {
  const doc = await this.getDoc(docRef);
  if (!doc.exists()) {
    throw new Error('Document not found');
  }
  
  const data = doc.data();
  const updates: any = {};
    
    if (permission === 'read' || permission === 'write') {
      // Convert single value to array if needed
      const currentAllowRead = data?.allowRead;
      if (Array.isArray(currentAllowRead)) {
        if (!currentAllowRead.includes(targetUserId)) {
          updates.allowRead = [...currentAllowRead, targetUserId];
        }
      } else {
        updates.allowRead = [currentAllowRead, targetUserId];
      }
    }
    
    if (permission === 'write') {
      const currentAllowWrite = data?.allowWrite;
      if (Array.isArray(currentAllowWrite)) {
        if (!currentAllowWrite.includes(targetUserId)) {
          updates.allowWrite = [...currentAllowWrite, targetUserId];
        }
      } else {
        updates.allowWrite = [currentAllowWrite, targetUserId];
      }
    }
    
    if (Object.keys(updates).length > 0) {
      await this.updateDoc(docRef, updates);
      logger.debug(`📤 Shared document ${docRef.path} with ${targetUserId} (${permission})`);
    }
  }
  
  /**
   * Helper to revoke access from a user
   */
  async revokeAccess(
    docRef: DocumentReference,
    targetUserId: string
  ): Promise<void> {
    const doc = await this.getDoc(docRef);
    if (!doc.exists()) {
      throw new Error('Document not found');
    }
    
    const data = doc.data();
    const updates: any = {};
    
    // Remove from allowRead
    const currentAllowRead = data?.allowRead;
    if (Array.isArray(currentAllowRead)) {
      updates.allowRead = currentAllowRead.filter((id: string) => id !== targetUserId);
    }
    
    // Remove from allowWrite
    const currentAllowWrite = data?.allowWrite;
    if (Array.isArray(currentAllowWrite)) {
      updates.allowWrite = currentAllowWrite.filter((id: string) => id !== targetUserId);
    }
    
    if (Object.keys(updates).length > 0) {
      await this.updateDoc(docRef, updates);
      logger.debug(`🚫 Revoked access to document ${docRef.path} from ${targetUserId}`);
    }
  }
}

/**
 * Factory function to create a scoped database instance
 */
export function getScopedDb(user: User | null): ScopedDatabase {
  const userContext = getUserContext(user);
  return new ScopedDatabase(userContext);
}

/**
 * Hook-friendly version that can be used in React components
 */
let currentScopedDb: ScopedDatabase | null = null;

export function updateScopedDb(user: User | null): ScopedDatabase {
  currentScopedDb = getScopedDb(user);
  return currentScopedDb;
}

export function getCurrentScopedDb(): ScopedDatabase {
  if (!currentScopedDb) {
    throw new Error('Scoped database not initialized. Make sure to call updateScopedDb first.');
  }
  return currentScopedDb;
}
